import { useAppDispatch, useAppSelector } from "app/hooks"
import { useEffect, useMemo, useState } from "react"
import {
  getBody,
  getBodyDisplayUnits,
  getLatestUserScan,
  updateBodyAndUnits,
  updateBodyDisplayUnits,
} from "../profileSlice"
import {
  IBodyDisplayUnits,
  IUpdateBodyAndUnitPayload,
} from "../profileTypes"
import { useFailed, useSuccess } from "features/notification/hooks"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import {
  SCAN_MESSAGE_NO_SCAN,
  SCAN_MESSAGE_STALE_SCAN,
  SCAN_MESSAGE_WEIGHT_CHANGED,
} from "config"
import { getDisplayHeightWithUnit } from "utils/heightConverter"
import {
  getBodyWeightValueFromGram,
  getDisplayWeightWithUnit,
} from "utils/weightConverter"
import { validateBodyHeight } from "utils/heightValidator"
import { validateBodyWeight } from "utils/weightValidator"

dayjs.extend(utc)

export const useBody = () => {
  const body = useAppSelector((state) => state.profile.body)
  const getBodyLoading = useAppSelector((state) => state.profile.getBodyLoading)
  const getBodyFailed = useAppSelector((state) => state.profile.getBodyFailed)

  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(getBody())
  }, [])

  return {
    body,
    getBodyLoading,
    getBodyFailed,
  }
}

export const useUpdateBodyAndUnitsWhenOnboarding = (callback?: () => void) => {
  const updateBodyAndUnitsLoading = useAppSelector(
    (state) => state.profile.updateBodyAndUnitsLoading,
  )
  const updateBodyAndUnitsFailed = useAppSelector(
    (state) => state.profile.updateBodyAndUnitsFailed,
  )
  const updateBodyAndUnitsSuccess = useAppSelector(
    (state) => state.profile.updateBodyAndUnitsSuccess,
  )

  const dispatch = useAppDispatch()

  const handleUpdateBodyAndUnits = (payload: IUpdateBodyAndUnitPayload) => {
    dispatch(updateBodyAndUnits(payload))
  }

  useFailed(updateBodyAndUnitsFailed)
  useSuccess(updateBodyAndUnitsSuccess, { message: "" }, callback)

  return {
    updateBodyAndUnitsLoading,
    updateBodyAndUnitsFailed,
    updateBodyAndUnitsSuccess,
    handleUpdateBodyAndUnits,
  }
}

export const useBodyDisplayUnits = () => {
  const bodyDisplayUnits = useAppSelector(
    (state) => state.profile.bodyDisplayUnits,
  )
  const getBodyDisplayUnitsLoading = useAppSelector(
    (state) => state.profile.getBodyDisplayUnitsLoading,
  )
  const getBodyDisplayUnitsFailed = useAppSelector(
    (state) => state.profile.getBodyDisplayUnitsFailed,
  )

  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(getBodyDisplayUnits())
  }, [])

  useFailed(getBodyDisplayUnitsFailed)

  return {
    bodyDisplayUnits,
    getBodyDisplayUnitsLoading,
    getBodyDisplayUnitsFailed,
  }
}

export const useUpdateBodyDisplayUnits = () => {
  const updateBodyDisplayUnitsLoading = useAppSelector(
    (state) => state.profile.updateBodyDisplayUnitsLoading,
  )
  const updateBodyDisplayUnitsFailed = useAppSelector(
    (state) => state.profile.updateBodyDisplayUnitsFailed,
  )
  const updateBodyDisplayUnitsSuccess = useAppSelector(
    (state) => state.profile.updateBodyDisplayUnitsSuccess,
  )

  const dispatch = useAppDispatch()

  const handleUpdateBodyDisplayUnits = (
    bodyDisplayUnits: IBodyDisplayUnits,
  ) => {
    dispatch(updateBodyDisplayUnits(bodyDisplayUnits))
  }

  useFailed(updateBodyDisplayUnitsFailed)

  return {
    updateBodyDisplayUnitsLoading,
    updateBodyDisplayUnitsSuccess,
    handleUpdateBodyDisplayUnits,
  }
}

export const useDisplayBodyWithUnits = (
  localBodyDisplayUnit?: IBodyDisplayUnits,
) => {
  const { body } = useBody()

  const bodyDisplayUnits = useAppSelector(
    (state) => state.profile.bodyDisplayUnits,
  )

  const unit = localBodyDisplayUnit ?? bodyDisplayUnits

  const displayedHeightWithUnit = getDisplayHeightWithUnit(unit, body?.height)
  const displayedWeightWithUnit = getDisplayWeightWithUnit(unit, body?.weight)

  return {
    displayedHeightWithUnit,
    displayedWeightWithUnit,
  }
}

export const useLatestUserScan = () => {
  const latestUserScan = useAppSelector(
    (state) => state.profile.latestUserScan,
  )
  const getLatestUserScanLoading = useAppSelector(
    (state) => state.profile.getLatestUserScanLoading,
  )

  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(getLatestUserScan())
  }, [])

  return {
    latestUserScan,
    getLatestUserScanLoading,
  }
}

export const useValidateBodyHeight = (
  bodyDisplayUnits: IBodyDisplayUnits,
  height: number,
) => {
  const [error, setError] = useState("")

  useEffect(() => {
    setError(validateBodyHeight(bodyDisplayUnits, height))
  }, [bodyDisplayUnits, height])

  return error
}

export const useValidateBodyWeight = (
  bodyDisplayUnits: IBodyDisplayUnits,
  weight: number,
) => {
  const [error, setError] = useState("")

  useEffect(() => {
    setError(validateBodyWeight(bodyDisplayUnits, weight))
  }, [bodyDisplayUnits, weight])

  return error
}

export const useScanMessage = (params: { gramWeight: number }) => {
  const { gramWeight } = params

  // Read from Redux state instead of calling useLatestUserScan (which triggers fetch)
  const latestUserScan = useAppSelector(
    (state) => state.profile.latestUserScan,
  )
  const getLatestUserScanLoading = useAppSelector(
    (state) => state.profile.getLatestUserScanLoading,
  )
  const lastMeasurementWeight = useAppSelector(
    (state) => state.profile.lastMeasurementWeight,
  )
  const bodyDisplayUnits = useAppSelector(
    (state) => state.profile.bodyDisplayUnits,
  )
  const getBodyDisplayUnitsLoading = useAppSelector(
    (state) => state.profile.getBodyDisplayUnitsLoading,
  )
  const getBodyLoading = useAppSelector(
    (state) => state.profile.getBodyLoading,
  )

  const scanMessage = useMemo(() => {
    if (getLatestUserScanLoading || getBodyDisplayUnitsLoading || getBodyLoading) {
      return null
    }

    if (!latestUserScan) {
      return SCAN_MESSAGE_NO_SCAN
    }

    const twoWeeksAgo = dayjs().subtract(14, "day")
    const isOlderThanTwoWeeks = dayjs
      .utc(latestUserScan.measuredAt)
      .local()
      .isBefore(twoWeeksAgo, "second")
    if (isOlderThanTwoWeeks) {
      return SCAN_MESSAGE_STALE_SCAN
    }

    // Convert both weights to display units and compare (what user sees)
    if (lastMeasurementWeight) {
      const measurementDisplayWeight = getBodyWeightValueFromGram(
        bodyDisplayUnits,
        lastMeasurementWeight,
      )
      const inputDisplayWeight = getBodyWeightValueFromGram(
        bodyDisplayUnits,
        gramWeight,
      )
      if (measurementDisplayWeight !== inputDisplayWeight) {
        return SCAN_MESSAGE_WEIGHT_CHANGED
      }
    }

    return null
  }, [
    getLatestUserScanLoading,
    getBodyDisplayUnitsLoading,
    getBodyLoading,
    latestUserScan,
    lastMeasurementWeight,
    bodyDisplayUnits,
    gramWeight,
  ])

  return scanMessage
}
